import { t } from "@lingui/core/macro";
import { type TimestampFormat } from "@t3tools/contracts/settings";

import { i18n } from "./i18n";

export function getTimestampFormatOptions(
  timestampFormat: TimestampFormat,
  includeSeconds: boolean,
): Intl.DateTimeFormatOptions {
  const baseOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    ...(includeSeconds ? { second: "2-digit" } : {}),
  };

  if (timestampFormat === "locale") {
    return baseOptions;
  }

  return {
    ...baseOptions,
    hour12: timestampFormat === "12-hour",
  };
}

const timestampFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getTimestampFormatter(
  timestampFormat: TimestampFormat,
  includeSeconds: boolean,
): Intl.DateTimeFormat {
  const cacheKey = `${i18n.locale}:${timestampFormat}:${includeSeconds ? "seconds" : "minutes"}`;
  const cachedFormatter = timestampFormatterCache.get(cacheKey);
  if (cachedFormatter) {
    return cachedFormatter;
  }

  const formatter = new Intl.DateTimeFormat(
    i18n.locale || undefined,
    getTimestampFormatOptions(timestampFormat, includeSeconds),
  );
  timestampFormatterCache.set(cacheKey, formatter);
  return formatter;
}

export function formatTimestamp(isoDate: string, timestampFormat: TimestampFormat): string {
  return getTimestampFormatter(timestampFormat, true).format(new Date(isoDate));
}

export function formatShortTimestamp(isoDate: string, timestampFormat: TimestampFormat): string {
  return getTimestampFormatter(timestampFormat, false).format(new Date(isoDate));
}

/**
 * Format a relative time string from an ISO date.
 * Returns `{ value: "20s", suffix: "ago" }` or `{ value: "just now", suffix: null }`
 * so callers can style the numeric portion independently.
 */
export function formatRelativeTime(isoDate: string): { value: string; suffix: string | null } {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  if (diffMs < 0) return { value: t`just now`, suffix: null };
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return { value: t`just now`, suffix: null };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { value: t`${minutes}m`, suffix: t`ago` };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { value: t`${hours}h`, suffix: t`ago` };
  const days = Math.floor(hours / 24);
  return { value: t`${days}d`, suffix: t`ago` };
}

export function formatRelativeTimeLabel(isoDate: string) {
  const relative = formatRelativeTime(isoDate);
  return relative.suffix ? `${relative.value} ${relative.suffix}` : relative.value;
}

/**
 * Relative elapsed duration since an ISO instant, without an "ago" suffix.
 * Useful for labels like "Connected for 3m".
 */
export function formatElapsedDurationLabel(isoDate: string, nowMs: number = Date.now()): string {
  const diffMs = nowMs - new Date(isoDate).getTime();
  if (diffMs <= 0) return t`just now`;

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return t`just now`;
  if (seconds < 60) return t`${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t`${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t`${hours}h`;

  const days = Math.floor(hours / 24);
  return t`${days}d`;
}

/**
 * Relative time until an ISO instant (e.g. expiry). Mirrors {@link formatRelativeTime} but for future times.
 */
export function formatRelativeTimeUntil(isoDate: string): { value: string; suffix: string | null } {
  const diffMs = new Date(isoDate).getTime() - Date.now();
  if (diffMs <= 0) return { value: t`Expired`, suffix: null };
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 5) return { value: t`Soon`, suffix: null };
  if (seconds < 60) return { value: t`${seconds}s`, suffix: t`left` };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { value: t`${minutes}m`, suffix: t`left` };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { value: t`${hours}h`, suffix: t`left` };
  const days = Math.floor(hours / 24);
  return { value: t`${days}d`, suffix: t`left` };
}

/**
 * Countdown for a future instant (e.g. link expiry): "Expires in 4m 12s", with second precision under one hour.
 * Pass `nowMs` when a parent tick drives re-renders so the diff matches that snapshot.
 */
export function formatExpiresInLabel(isoDate: string, nowMs: number = Date.now()): string {
  const diffMs = new Date(isoDate).getTime() - nowMs;
  if (diffMs <= 0) return t`Expired`;

  const totalSeconds = Math.floor(diffMs / 1000);
  if (totalSeconds < 5) return t`Expires in a moment`;
  if (totalSeconds < 60) return t`Expires in ${formatDuration(0, 0, 0, totalSeconds)}`;

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return t`Expires in ${formatDuration(0, 0, minutes, seconds)}`;
  }

  if (totalSeconds < 86_400) {
    const hours = Math.floor(totalSeconds / 3600);
    const rem = totalSeconds % 3600;
    return t`Expires in ${formatDuration(0, hours, Math.floor(rem / 60), rem % 60)}`;
  }

  const days = Math.floor(totalSeconds / 86_400);
  const remAfterDays = totalSeconds % 86_400;
  const hours = Math.floor(remAfterDays / 3600);
  const rem = remAfterDays % 3600;
  return t`Expires in ${formatDuration(days, hours, Math.floor(rem / 60), rem % 60)}`;
}

function formatDuration(days: number, hours: number, minutes: number, seconds: number): string {
  const parts: string[] = [];
  if (days > 0) parts.push(t`${days}d`);
  if (hours > 0) parts.push(t`${hours}h`);
  if (minutes > 0) parts.push(t`${minutes}m`);
  if (seconds > 0) parts.push(t`${seconds}s`);
  if (parts.length === 0) parts.push(t`${seconds}s`);
  return parts.join(" ");
}
